import { FileUpload } from '../models/file-upload';
import { transferTray } from './transfer-tray.service';

/**
 * Take the file the user just produced to the next tool, without them touching their disk.
 *
 * The result screen could only ever *link* to the next tool. The output stayed behind, so
 * "compress this GIF I just made" meant downloading their own result and uploading it again —
 * two manual steps and a trip through the file manager, on the exact screen where they had just
 * succeeded. T3.1 measured this as 3.5% of continuations (the rest are same-tool re-runs, which
 * Re-edit covers), but it is the 3.5% where the product visibly gives up.
 *
 * **Why it fetches the output rather than using `/uploads/from-output`.** That endpoint exists
 * and is the efficient answer — it hardlinks the output into the upload directory server-side and
 * hands back a registered input, costing zero bytes. What it cannot do yet is *stage* the result:
 * the destination stages through `fileSelected`, which requires real `File` instances
 * (`f instanceof File`), so a server handle needs a staging path that does not exist. Building
 * that means new branches in `FileUploadManager` — the upload path, where a regression is most
 * expensive.
 *
 * So this carries the bytes through the tray instead, and the destination stages them through
 * exactly the same path a drop takes. Against the status quo it is not a bandwidth regression:
 * the user was going to download and re-upload the same file by hand. It is one download they no
 * longer perform themselves, and zero new code in the upload path.
 *
 * See docs/proposed/CHAINING_DESIGN.md and RETENTION_FIXES.md T3.4.
 */

/** Anything larger is not worth pulling through the browser to save a file-picker trip. */
export const CHAIN_MAX_BYTES = 100 * 1024 * 1024;

export type ChainFailure = 'too-large' | 'unavailable' | 'tray-unavailable';

export interface ChainResult {
    /** Destination URL to navigate to — carries the file when `carried` is true. */
    href: string;
    carried: boolean;
    reason?: ChainFailure;
}

/**
 * Build the destination URL for a continuation, carrying the output when possible.
 *
 * Never throws and never blocks navigation: every failure returns a plain link to the
 * destination, which is exactly the behaviour before this existed. The caller reports the
 * difference to the user rather than silently landing them on an empty uploader.
 */
export async function chainToTool(
    fileUpload: FileUpload,
    slug: string,
    downloadUrl: string | null,
): Promise<ChainResult> {
    const href = `/${slug.replace(/^\//, '')}`;
    try {
        if (!downloadUrl) return { href, carried: false, reason: 'unavailable' };

        const size = Number(fileUpload?.result?.outputSize ?? 0);
        if (size > CHAIN_MAX_BYTES) return { href, carried: false, reason: 'too-large' };

        const response = await fetch(downloadUrl);
        if (!response.ok) return { href, carried: false, reason: 'unavailable' };
        const blob = await response.blob();
        // Guard again on the real size: outputSize is metadata and can be absent or stale.
        if (blob.size > CHAIN_MAX_BYTES) return { href, carried: false, reason: 'too-large' };

        let file = new File([blob], readableOutputName(fileUpload), {
            type: blob.type || 'application/octet-stream',
        });
        // J5-1: neither the type nor the output name said what it is ("photo.image"): the bytes do.
        if (!/\.[a-z0-9]+$/i.test(file.name) || GENERIC_TYPES.has(extOf(file.name))) {
            file = (await renameBySniff(file)) || file;
        }
        const xferId = await transferTray.put([file], fileUpload.uniqueProcessType || '', fileUpload.name || '');
        if (!xferId) return { href, carried: false, reason: 'tray-unavailable' };
        // J5-5: "Use a recent file" on the next page offers this output, not only the original upload.
        try { await transferTray.remember([file], fileUpload.uniqueProcessType || '', fileUpload.name || ''); } catch { /* convenience */ }

        return { href: `${href}?xfer=${encodeURIComponent(xferId)}`, carried: true };
    } catch {
        // Offline, blocked by the network, aborted — all the same to the user: they still get
        // the tool, just without the file already in it.
        return { href, carried: false, reason: 'unavailable' };
    }
}

/**
 * A name the user recognises for the output.
 *
 * `fileUpload.name` is the input's name; the output shares it but not its extension, so
 * "holiday.gif" converted to MP4 must arrive as "holiday.mp4" or the destination rejects it on
 * extension before the user sees anything.
 */
export function readableOutputName(fileUpload: FileUpload): string {
    const base = (fileUpload?.name || 'file').replace(/\.[^.]+$/, '');
    let ext = `${fileUpload?.toType || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
    // J5-1: generic pages (image-compressor, jpg-/webp-/eps-converter) have toType 'image'; the
    // real extension is on the output file the server wrote.
    if (!ext || GENERIC_TYPES.has(ext)) {
        const output = (fileUpload as any)?.result?.output;
        const outExt = typeof output === 'string' ? extOf(output) : '';
        ext = outExt && !GENERIC_TYPES.has(outExt) ? outExt : ext;
    }
    return ext ? `${base}.${ext}` : base;
}

/** toType values that name a family, not a file extension. */
const GENERIC_TYPES = new Set(['image', 'images', 'video', 'audio', 'document', 'file', 'any']);
const extOf = (name: string) => (/\.([a-z0-9]+)$/i.exec(name)?.[1] || '').toLowerCase();

/**
 * Image extension from the first bytes (J5-1). Generic pages (image-compressor, jpg-converter, ...)
 * have toType 'image', so a carried output can arrive named "photo.image"; its bytes still say what
 * it is. Covers what the image pages open; null for anything else.
 */
export function sniffImageExtension(h: Uint8Array): string | null {
    const at = (i: number, ...b: number[]) => b.every((v, k) => h[i + k] === v);
    const ascii = (i: number, n: number) => String.fromCharCode(...Array.from(h.subarray(i, i + n)));
    if (h.length < 4) return null;
    if (at(0, 0xff, 0xd8, 0xff)) return 'jpg';
    if (at(0, 0x89, 0x50, 0x4e, 0x47)) return 'png';
    if (ascii(0, 4) === 'GIF8') return 'gif';
    if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') return 'webp';
    if (ascii(0, 2) === 'BM') return 'bmp';
    if (at(0, 0x49, 0x49, 0x2a, 0x00) || at(0, 0x4d, 0x4d, 0x00, 0x2a)) return 'tiff';
    if (at(0, 0x00, 0x00, 0x01, 0x00)) return 'ico';
    if (ascii(0, 4) === '8BPS') return 'psd';
    if (ascii(4, 4) === 'ftyp') {
        const brand = ascii(8, 4);
        if (/^avi[fs]$/.test(brand)) return 'avif';
        if (/^(heic|heix|heim|heis|hevc|hevx|mif1|msf1)$/.test(brand)) return 'heic';
    }
    if (ascii(0, 4) === '%PDF') return 'pdf';
    return null;
}

const MIME_BY_EXT: Record<string, string> = {
    jpg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp', tiff: 'image/tiff',
    ico: 'image/x-icon', psd: 'image/vnd.adobe.photoshop', avif: 'image/avif', heic: 'image/heic', pdf: 'application/pdf',
};

async function readHead(f: Blob, n = 32): Promise<Uint8Array> {
    const part = f.slice(0, n);
    if (typeof (part as any).arrayBuffer === 'function') return new Uint8Array(await part.arrayBuffer());
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer));
        r.onerror = () => reject(r.error);
        r.readAsArrayBuffer(part);
    });
}

/** The file renamed to the extension its bytes say, or null when the bytes are not a known image. */
export async function renameBySniff(f: File): Promise<File | null> {
    try {
        const ext = sniffImageExtension(await readHead(f));
        if (!ext) return null;
        const base = f.name.replace(/\.[^.]*$/, '') || 'file';
        return new File([f], `${base}.${ext}`, { type: MIME_BY_EXT[ext] || f.type, lastModified: f.lastModified });
    } catch { return null; }
}
